<template>
  <div class="member-payment">
    <el-card class="payment-card">
      <template #header>
        <div class="card-header">
          <h2>会员支付</h2>
        </div>
      </template>

      <!-- 会员信息确认 -->
      <div class="member-info-section">
        <h3>会员信息确认</h3>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="会员类型">
            {{ memberTypeText }}
          </el-descriptions-item>
          <el-descriptions-item label="会员等级" v-if="isIndividual">
            {{ memberLevelText }}
          </el-descriptions-item>
          <el-descriptions-item label="支付金额">
            <span class="amount">{{ amountText }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="有效期">
            1年
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 权益说明 -->
      <div class="benefits-section">
        <h3>会员权益</h3>
        <el-alert
          type="success"
          :closable="false"
          show-icon
        >
          <template #title>
            <ul class="benefits-list">
              <li v-for="(benefit, index) in benefits" :key="index">
                {{ benefit }}
              </li>
            </ul>
          </template>
        </el-alert>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <el-button size="large" @click="goBack">
          返回修改
        </el-button>
        <el-button
          type="primary"
          size="large"
          :loading="submitting"
          @click="handlePay"
        >
          {{ submitting ? '处理中...' : '确认支付' }}
        </el-button>
      </div>
    </el-card>

    <!-- 支付弹窗 -->
    <PaymentModal
      v-model="paymentVisible"
      :order-info="orderInfo"
      @confirm="handlePaymentConfirm"
      @close="handlePaymentClose"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import PaymentModal from '@/components/PaymentModal.vue'
import memberApi from '@/api/member'
import { 
  MEMBER_TYPES, 
  MEMBER_LEVELS, 
  MEMBER_TYPE_NAMES, 
  MEMBER_LEVEL_NAMES,
  ORDER_TYPES,
  PAYMENT_METHODS
} from '@/utils/constants'

export default {
  name: 'MemberPayment',
  
  components: {
    PaymentModal
  },
  
  data() {
    return {
      memberType: null,
      memberLevel: null,
      submitting: false,
      paymentVisible: false,
      orderInfo: {
        type: ORDER_TYPES.MEMBER,
        name: '',
        amount: 0,
        orderId: null
      }
    }
  },
  
  computed: {
    isIndividual() {
      return this.memberType === MEMBER_TYPES.INDIVIDUAL
    },
    
    memberTypeText() {
      return MEMBER_TYPE_NAMES[this.memberType] || '-'
    },
    
    memberLevelText() {
      return MEMBER_LEVEL_NAMES[this.memberLevel] || '-'
    },
    
    amountText() {
      if (this.memberType === MEMBER_TYPES.INDIVIDUAL) {
        return this.memberLevel === MEMBER_LEVELS.NORMAL ? '¥100' : '¥300'
      }
      return '面议'
    },
    
    benefits() {
      const commonBenefits = ['浏览课程信息', '购买课程']
      
      if (this.memberType === MEMBER_TYPES.INDIVIDUAL) {
        if (this.memberLevel === MEMBER_LEVELS.REGISTERED) {
          return [...commonBenefits, '个人信息展示', '优先参与活动']
        }
        return commonBenefits
      } else if (this.memberType === MEMBER_TYPES.ORGANIZATION) {
        return ['机构信息展示', '社会服务搜索', '查看完整会员信息']
      } else if (this.memberType === MEMBER_TYPES.DIRECTOR) {
        return [...commonBenefits, '发布新闻', '管理新闻', '参与协会决策']
      }
      
      return []
    }
  },
  
  mounted() {
    // 从路由参数获取会员类型和等级
    this.memberType = parseInt(this.$route.query.type)
    this.memberLevel = this.$route.query.level ? parseInt(this.$route.query.level) : null
    
    // 验证参数
    if (!this.memberType) {
      this.$message.error('参数错误')
      this.$router.push('/user/member-type-select')
    }
  },
  
  methods: {
    ...mapActions('auth', ['refreshMemberInfo']),
    
    /**
     * 确认支付
     */
    async handlePay() {
      this.submitting = true
      
      try {
        // 创建会员订单
        const res = await memberApi.createMemberOrder({
          member_type: this.memberType,
          member_level: this.memberLevel
        })
        
        if (res && res.code === 2000) {
          // 设置订单信息
          this.orderInfo = {
            type: ORDER_TYPES.MEMBER,
            name: `${this.memberTypeText}${this.isIndividual ? ' - ' + this.memberLevelText : ''}`,
            amount: res.data.amount || 0,
            orderId: res.data.order_id
          }
          
          // 显示支付弹窗
          this.paymentVisible = true
        }
      } catch (error) {
        console.error('创建订单失败:', error)
      } finally {
        this.submitting = false
      }
    },
    
    /**
     * 处理支付确认
     */
    async handlePaymentConfirm({ paymentMethod, orderInfo }) {
      try {
        // 提交支付信息
        const res = await memberApi.submitPayment({
          order_id: orderInfo.orderId,
          payment_method: paymentMethod
        })
        
        if (res && res.code === 2000) {
          this.paymentVisible = false
          
          // 根据支付方式显示不同提示
          if (paymentMethod === PAYMENT_METHODS.BANK) {
            this.$alert(
              '您的转账信息已提交，我们会在1-3个工作日内完成审核。审核通过后会员权限将自动生效。',
              '提交成功',
              {
                confirmButtonText: '我知道了',
                type: 'success',
                callback: () => {
                  this.goToMemberInfo()
                }
              }
            )
          } else {
            this.$message.success('支付成功')
            // 刷新会员信息
            await this.refreshMemberInfo(res.data.member)
            this.goToMemberInfo()
          }
        }
      } catch (error) {
        console.error('支付失败:', error)
      }
    },
    
    /**
     * 关闭支付弹窗
     */
    handlePaymentClose() {
      this.paymentVisible = false
    },
    
    /**
     * 跳转到会员信息完善页面
     */
    goToMemberInfo() {
      this.$router.push('/user/member-info')
    },
    
    /**
     * 返回
     */
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style lang="scss" scoped>
.member-payment {
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f5f7fa;
  
  .payment-card {
    max-width: 800px;
    margin: 0 auto;
    
    .card-header {
      text-align: center;
      
      h2 {
        margin: 0;
        font-size: 24px;
        color: #303133;
      }
    }
    
    .member-info-section,
    .benefits-section {
      margin-bottom: 30px;
      
      h3 {
        margin: 0 0 15px 0;
        font-size: 18px;
        color: #303133;
      }
      
      .amount {
        color: #f56c6c;
        font-size: 24px;
        font-weight: bold;
      }
    }
    
    .benefits-list {
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
        padding: 5px 0;
        
        &:before {
          content: '✓ ';
          color: #67c23a;
          font-weight: bold;
          margin-right: 5px;
        }
      }
    }
    
    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 20px;
      padding-top: 30px;
      border-top: 1px solid #e4e7ed;
    }
  }
}

@media (max-width: 768px) {
  .member-payment {
    padding: 20px 10px;
    
    .payment-card {
      .action-buttons {
        flex-direction: column;
        
        .el-button {
          width: 100%;
        }
      }
    }
  }
}
</style>
